/**
 * POI／飯店清單 /pois（20260611 反饋批次）
 * 篩選列（region/zone/category）＋表格＋新增/編輯 modal（region→zone 連動）。
 */
import { useState } from "react";
import { Plus, Pencil, Power, PowerOff } from "lucide-react";
import { trpc } from "../../lib/trpc";
import { useLanguage } from "../../contexts/LanguageContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input, Select } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { REGIONS, type Loc, type Region } from "../../../../shared/const";
import { POI_CATEGORIES, ZONES, type PoiCategory, type ZoneCode } from "../../../../shared/booking";
import { ActiveBadge, REGION_LOC, THEAD_ROW, zoneLoc } from "./base-data-shared";

const CATEGORY_LOC: Record<PoiCategory, Loc> = {
  hotel: { "zh-TW": "飯店", "en-US": "Hotel", "zh-CN": "饭店" },
  attraction: { "zh-TW": "景點", "en-US": "Attraction", "zh-CN": "景点" },
  transport: { "zh-TW": "交通樞紐", "en-US": "Transport", "zh-CN": "交通枢纽" },
};

type Row = {
  id: number;
  region: Region;
  zone: string;
  category: PoiCategory;
  name: string;
  address: string | null;
  isActive: "yes" | "no";
};

export default function Pois() {
  const { t } = useLanguage();
  const listQ = trpc.poi.list.useQuery();
  const rows = (listQ.data ?? []) as Row[];

  const [fRegion, setFRegion] = useState<"all" | Region>("all");
  const [fZone, setFZone] = useState<"all" | ZoneCode>("all");
  const [fCategory, setFCategory] = useState<"all" | PoiCategory>("all");

  const setActive = trpc.poi.setActive.useMutation({ onSuccess: () => listQ.refetch() });

  const zoneOptions = fRegion === "all" ? ZONES : ZONES.filter((z) => z.region === fRegion);
  const filtered = rows.filter(
    (r) =>
      (fRegion === "all" || r.region === fRegion) &&
      (fZone === "all" || r.zone === fZone) &&
      (fCategory === "all" || r.category === fCategory),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight">
            {t({ "zh-TW": "POI 與飯店", "en-US": "POIs & Hotels", "zh-CN": "POI 与饭店" })}
          </h1>
          <p className="text-[13px] text-muted-foreground">
            {t({
              "zh-TW": "建單地點選單來源：飯店 / 景點 / 交通樞紐，依 region 與 zone 歸檔。",
              "en-US": "Booking location options: hotels / attractions / transport hubs, filed by region and zone.",
              "zh-CN": "建单地点选单来源：饭店 / 景点 / 交通枢纽，依 region 与 zone 归档。",
            })}
          </p>
        </div>
        <PoiDialog onDone={() => listQ.refetch()} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          className="w-36"
          value={fRegion}
          onChange={(e) => {
            setFRegion(e.target.value as "all" | Region);
            setFZone("all");
          }}
        >
          <option value="all">{t({ "zh-TW": "全部區域", "en-US": "All regions", "zh-CN": "全部区域" })}</option>
          {REGIONS.map((r) => (
            <option key={r} value={r}>
              {t(REGION_LOC[r])}
            </option>
          ))}
        </Select>
        <Select className="w-36" value={fZone} onChange={(e) => setFZone(e.target.value as "all" | ZoneCode)}>
          <option value="all">{t({ "zh-TW": "全部地區", "en-US": "All zones", "zh-CN": "全部地区" })}</option>
          {zoneOptions.map((z) => (
            <option key={z.code} value={z.code}>
              {t(z.label)}
            </option>
          ))}
        </Select>
        <Select className="w-36" value={fCategory} onChange={(e) => setFCategory(e.target.value as "all" | PoiCategory)}>
          <option value="all">{t({ "zh-TW": "全部分類", "en-US": "All categories", "zh-CN": "全部分类" })}</option>
          {POI_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {t(CATEGORY_LOC[c])}
            </option>
          ))}
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {listQ.isLoading ? (
            <div className="h-24 animate-pulse rounded-lg bg-muted/40" />
          ) : filtered.length === 0 ? (
            <p className="p-8 text-center text-[13px] text-muted-foreground">
              {t({ "zh-TW": "查無符合條件的 POI。", "en-US": "No POIs match the filters.", "zh-CN": "查无符合条件的 POI。" })}
            </p>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className={THEAD_ROW}>
                  <th className="p-3">{t({ "zh-TW": "區域", "en-US": "Region", "zh-CN": "区域" })}</th>
                  <th className="p-3">{t({ "zh-TW": "地區", "en-US": "Zone", "zh-CN": "地区" })}</th>
                  <th className="p-3">{t({ "zh-TW": "分類", "en-US": "Category", "zh-CN": "分类" })}</th>
                  <th className="p-3">{t({ "zh-TW": "名稱", "en-US": "Name", "zh-CN": "名称" })}</th>
                  <th className="p-3">{t({ "zh-TW": "地址", "en-US": "Address", "zh-CN": "地址" })}</th>
                  <th className="p-3">{t({ "zh-TW": "狀態", "en-US": "Status", "zh-CN": "状态" })}</th>
                  <th className="p-3 text-right">{t({ "zh-TW": "操作", "en-US": "Action", "zh-CN": "操作" })}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 last:border-0">
                    <td className="p-3">{t(REGION_LOC[r.region])}</td>
                    <td className="p-3">{t(zoneLoc(r.zone))}</td>
                    <td className="p-3">{t(CATEGORY_LOC[r.category])}</td>
                    <td className="p-3 font-medium">{r.name}</td>
                    <td className="p-3 text-muted-foreground">{r.address ?? "—"}</td>
                    <td className="p-3"><ActiveBadge active={r.isActive} /></td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <PoiDialog poi={r} onDone={() => listQ.refetch()} />
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={setActive.isPending}
                        onClick={() => setActive.mutate({ id: r.id, isActive: r.isActive === "yes" ? "no" : "yes" })}
                      >
                        {r.isActive === "yes" ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
                        {r.isActive === "yes"
                          ? t({ "zh-TW": "停用", "en-US": "Disable", "zh-CN": "停用" })
                          : t({ "zh-TW": "啟用", "en-US": "Enable", "zh-CN": "启用" })}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PoiDialog({ poi, onDone }: { poi?: Row; onDone: () => void }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [region, setRegion] = useState<Region>(poi?.region ?? "taiwan");
  const [zone, setZone] = useState<ZoneCode>((poi?.zone as ZoneCode) ?? "taipei");
  const [category, setCategory] = useState<PoiCategory>(poi?.category ?? "hotel");
  const [name, setName] = useState(poi?.name ?? "");
  const [address, setAddress] = useState(poi?.address ?? "");

  const zoneOptions = ZONES.filter((z) => z.region === region);

  const m = trpc.poi.upsert.useMutation({
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });

  // 開啟時從 props/初始值重新同步草稿，避免殘留上一次新增/取消的輸入
  const changeOpen = (o: boolean) => {
    setOpen(o);
    if (o) {
      setRegion(poi?.region ?? "taiwan");
      setZone((poi?.zone as ZoneCode) ?? "taipei");
      setCategory(poi?.category ?? "hotel");
      setName(poi?.name ?? "");
      setAddress(poi?.address ?? "");
    }
  };

  const changeRegion = (r: Region) => {
    setRegion(r);
    const first = ZONES.find((z) => z.region === r);
    if (first) setZone(first.code);
  };

  const save = () =>
    m.mutate({
      id: poi?.id,
      region,
      zone,
      category,
      name: name.trim(),
      address: address.trim() === "" ? null : address.trim(),
    });

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogTrigger asChild>
        {poi ? (
          <Button variant="ghost" size="sm">
            <Pencil className="h-3.5 w-3.5" />
            {t({ "zh-TW": "編輯", "en-US": "Edit", "zh-CN": "编辑" })}
          </Button>
        ) : (
          <Button size="sm">
            <Plus className="h-3.5 w-3.5" />
            {t({ "zh-TW": "新增 POI", "en-US": "New POI", "zh-CN": "新增 POI" })}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {poi
              ? t({ "zh-TW": "編輯 POI", "en-US": "Edit POI", "zh-CN": "编辑 POI" })
              : t({ "zh-TW": "新增 POI", "en-US": "New POI", "zh-CN": "新增 POI" })}
          </DialogTitle>
          <DialogDescription>
            {t({
              "zh-TW": "zone 選項依區域連動，需與區域一致。",
              "en-US": "Zone options follow the selected region.",
              "zh-CN": "zone 选项依区域联动，需与区域一致。",
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "區域", "en-US": "Region", "zh-CN": "区域" })}</Label>
              <Select value={region} onChange={(e) => changeRegion(e.target.value as Region)}>
                {REGIONS.map((r) => (
                  <option key={r} value={r}>
                    {t(REGION_LOC[r])}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "地區", "en-US": "Zone", "zh-CN": "地区" })}</Label>
              <Select value={zone} onChange={(e) => setZone(e.target.value as ZoneCode)}>
                {zoneOptions.map((z) => (
                  <option key={z.code} value={z.code}>
                    {t(z.label)}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "分類", "en-US": "Category", "zh-CN": "分类" })}</Label>
              <Select value={category} onChange={(e) => setCategory(e.target.value as PoiCategory)}>
                {POI_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {t(CATEGORY_LOC[c])}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "名稱", "en-US": "Name", "zh-CN": "名称" })}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t({ "zh-TW": "地址", "en-US": "Address", "zh-CN": "地址" })}</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} />
          </div>
          {m.error && <p className="text-[12px] text-destructive">{m.error.message}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}
          </Button>
          <Button onClick={save} disabled={m.isPending || name.trim() === ""}>
            {t({ "zh-TW": "儲存", "en-US": "Save", "zh-CN": "保存" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
